/**
 * MyFreeLessons.jsx — Lane 3B (Feb 2026).
 *
 * Signed-in FREE-tier surface: lists the user's own free lessons
 * (from GET /api/free-lesson/mine), newest first, with quota badge
 * pulled from GET /api/free-lesson/quota. Each row links to the
 * existing /free-lesson/:token render surface — no duplication of
 * the render logic.
 *
 * Paid users landing here get a soft redirect nudge to their real
 * library, and the empty state routes free users back to the
 * homepage form to generate their first lesson.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight,
  BookOpen,
  Loader2,
  Sparkles,
  Calendar,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import {
  fetchMyFreeLessons,
  fetchFreeLessonQuota,
  deleteMyFreeLesson,
} from "../lib/freeLessonApi";
import FreeLessonForm from "../components/FreeLessonForm";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-CA", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch (_e) {
    return "";
  }
};

const MyFreeLessons = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isPaid = !!(user?.subscription?.is_active || user?.is_admin);

  const [lessons, setLessons] = useState(null); // null = loading, [] = empty
  const [quota, setQuota] = useState(null);
  const [error, setError] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // token or null
  const [deletingToken, setDeletingToken] = useState(null);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!user) {
      navigate("/", { replace: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [ls, q] = await Promise.all([
          fetchMyFreeLessons(),
          fetchFreeLessonQuota(),
        ]);
        if (cancelled) return;
        setLessons(Array.isArray(ls?.lessons) ? ls.lessons : []);
        setQuota(q || null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.detail || "We couldn't load your free lessons.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user, navigate]);

  const badgeText = useMemo(() => {
    if (!quota) return "";
    if (quota.unlimited) return "Full engine — unlimited";
    // Lane 4 — show BOTH the monthly generate remaining AND the saved
    // count so the user always knows which cap is biting. Feb 2026
    // (mid-month): the third community-PDF gauge was retired along
    // with free-user community access.
    const gen = `${quota.remaining} of ${quota.limit} generations left`;
    const savedUsed = quota.saved_used ?? 0;
    const savedLimit = quota.saved_limit ?? 15;
    const saved = `${savedUsed} of ${savedLimit} saved`;
    return `${gen} · ${saved}`;
  }, [quota]);

  const handleDeleteConfirmed = async (token) => {
    setDeletingToken(token);
    try {
      await deleteMyFreeLesson(token);
      setLessons((current) =>
        (current || []).filter((row) => row.token !== token),
      );
      // Optimistically bump the local saved gauge so the badge updates
      // without a network round-trip. The /quota re-read on next page
      // load will reconcile.
      setQuota((q) =>
        q && !q.unlimited && typeof q.saved_used === "number"
          ? {
              ...q,
              saved_used: Math.max(0, q.saved_used - 1),
              saved_remaining:
                typeof q.saved_remaining === "number"
                  ? q.saved_remaining + 1
                  : q.saved_remaining,
            }
          : q,
      );
      toast.success("Removed from your free lessons.");
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Couldn't delete that lesson — try again in a moment.",
      );
    } finally {
      setDeletingToken(null);
      setPendingDelete(null);
    }
  };

  if (authLoading || lessons === null) {
    return (
      <div
        data-testid="my-free-lessons-loading"
        className="min-h-[60vh] flex items-center justify-center text-[#4a6984]"
      >
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="ml-2 text-sm font-semibold">Loading your free lessons…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <p data-testid="my-free-lessons-error" className="text-sm text-red-600 font-semibold">{error}</p>
      </div>
    );
  }

  return (
    <div
      data-testid="my-free-lessons-page"
      className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
    >
      {/* Header */}
      <div className="mb-8 sm:mb-10">
        <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
          Your Free Tier
        </p>
        <h1 className="mt-2 font-black text-3xl sm:text-4xl tracking-tight text-[#1c2d3d]">
          My free lessons
        </h1>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          {quota ? (
            <span
              data-testid="my-free-lessons-quota-badge"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] ${
                quota.unlimited
                  ? "bg-[#96ded1]/20 text-[#1c2d3d]"
                  : quota.remaining === 0
                    ? "bg-[#fdf7e6] text-[#7a5a12] border border-[#e6a63a]/40"
                    : "bg-[#f4f5f7] text-[#4a6984] border border-[#4a6984]/15"
              }`}
            >
              <Calendar className="h-3.5 w-3.5" />
              {badgeText}
            </span>
          ) : null}
          {isPaid ? (
            <Link
              to="/library"
              className="text-xs font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] inline-flex items-center gap-1"
            >
              Go to my library
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          ) : null}
        </div>
      </div>

      {/* Lane 5 fix (Feb 2026) — the saved list renders FIRST, above
          the fold. The "generate another" surface lives BELOW so a
          teacher returning to `/my-free-lessons` immediately sees
          their library, not a generator. Empty state also renders
          here (0 saved) so the teacher sees "no lessons yet" before
          the generator prompt. */}

      {/* Empty state */}
      {lessons.length === 0 ? (
        <div
          data-testid="my-free-lessons-empty"
          className="rounded-2xl border border-[#4a6984]/15 bg-white p-8 sm:p-10 shadow-sm text-center"
        >
          <BookOpen className="h-8 w-8 text-[#96ded1] mx-auto" strokeWidth={2.2} />
          <p className="mt-3 font-extrabold text-lg text-[#1c2d3d]">
            No saved lessons yet.
          </p>
          <p className="mt-1 text-sm text-[#4a6984]/85 font-medium leading-relaxed">
            Generate a lesson below and click <strong>Save</strong> — the
            ones you save will show up here.
          </p>
        </div>
      ) : (
        <ul
          data-testid="my-free-lessons-list"
          className="space-y-3 sm:space-y-4"
        >
          {lessons.map((l) => {
            const isConfirming = pendingDelete === l.token;
            const isDeleting = deletingToken === l.token;
            return (
              <li key={l.token}>
                <div
                  data-testid={`my-free-lessons-row-${l.token}`}
                  className="group relative rounded-xl border border-[#4a6984]/15 bg-white p-4 sm:p-5 shadow-sm hover:border-[#96ded1] hover:shadow-md transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Link
                      to={`/free-lesson/${encodeURIComponent(l.token)}`}
                      data-testid={`my-free-lessons-open-${l.token}`}
                      className="min-w-0 flex-1 no-underline"
                    >
                      <p className="font-extrabold text-base sm:text-lg text-[#1c2d3d] truncate">
                        {l.activity_name}
                      </p>
                      <p className="mt-1 text-xs sm:text-sm text-[#4a6984]/85 font-medium">
                        {[l.grade, l.subject, l.transferable_skill]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                      {l.expectation_code ? (
                        <p className="mt-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#96ded1]">
                          Expectation {l.expectation_code}
                        </p>
                      ) : null}
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/60 hidden sm:inline">
                        {formatDate(l.created_at)}
                      </span>
                      <ChevronRight className="h-4 w-4 text-[#4a6984]/60" />
                      <button
                        type="button"
                        onClick={() => setPendingDelete(l.token)}
                        disabled={isDeleting}
                        aria-label={`Delete ${l.activity_name}`}
                        data-testid={`my-free-lessons-delete-${l.token}`}
                        className="ml-1 h-8 w-8 inline-flex items-center justify-center rounded-full border border-[#4a6984]/15 text-[#4a6984]/70 hover:text-[#c0392b] hover:border-[#c0392b]/50 hover:bg-[#fdecea] transition-colors disabled:opacity-50"
                      >
                        {isDeleting ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <X className="h-4 w-4" strokeWidth={2.4} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirm micro-modal — inline; keeps the row focused
                      and avoids a fullscreen dialog for a low-stakes
                      destructive action. */}
                  {isConfirming ? (
                    <div
                      data-testid={`my-free-lessons-confirm-${l.token}`}
                      className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#c0392b]/25 bg-[#fdecea] p-3"
                    >
                      <p className="text-xs font-extrabold text-[#7a2419] leading-snug">
                        Remove this lesson from your free-tier library? Frees one saved slot.
                      </p>
                      <div className="flex gap-2 ml-auto">
                        <button
                          type="button"
                          onClick={() => setPendingDelete(null)}
                          data-testid={`my-free-lessons-cancel-${l.token}`}
                          className="rounded-md border border-[#4a6984]/25 bg-white px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:bg-[#f4f5f7]"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteConfirmed(l.token)}
                          disabled={isDeleting}
                          data-testid={`my-free-lessons-confirm-delete-${l.token}`}
                          className="inline-flex items-center gap-1.5 rounded-md bg-[#c0392b] px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#a5321f] disabled:opacity-60"
                        >
                          {isDeleting ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : null}
                          Delete
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Lane 5 fix (Feb 2026) — generator moved BELOW the saved list
          so `/my-free-lessons` reads as a library first, generator
          second. Visible only for signed-in free users (paid users
          have the full Generator). The embedded FreeLessonForm handles
          auth branching internally — for a free user it POSTs to
          /free-lesson/authenticated, decrements the local quota, and
          navigates to /free-lesson/:token on success. */}
      {!isPaid && lessons !== null ? (
        <div
          data-testid="my-free-lessons-generator"
          className="mt-8 sm:mt-10 pt-8 sm:pt-10 border-t border-[#4a6984]/15"
        >
          <p className="text-[10px] sm:text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#4a6984]/70">
            {lessons.length === 0 ? "Get started" : "Make another"}
          </p>
          <h2 className="mt-2 font-black text-2xl sm:text-3xl tracking-tight text-[#1c2d3d]">
            {lessons.length === 0
              ? "Generate your first free lesson"
              : "Generate another free lesson"}
          </h2>
          <p className="mt-1.5 text-sm text-[#4a6984]/85 font-medium">
            {quota && !quota.unlimited
              ? `${quota.remaining} of ${quota.limit} left this month.`
              : null}
          </p>
          <div className="mt-5">
            <FreeLessonForm />
          </div>
        </div>
      ) : null}

      {/* Under-list nudge (free users, not at cap) */}
      {!isPaid && quota && !quota.unlimited && quota.remaining > 0 && lessons.length > 0 ? (
        <div className="mt-8 rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7] p-5 sm:p-6">
          <p className="text-sm font-extrabold text-[#1c2d3d] leading-snug">
            {quota.remaining} free lesson{quota.remaining === 1 ? "" : "s"} left this month.
          </p>
          <p className="mt-1 text-xs text-[#4a6984]/85 font-medium leading-relaxed">
            Resets on the 1st. When you&apos;re ready for the full engine — slide decks, exports,
            full differentiation, planner — start a 7-day free trial.
          </p>
          <Link
            to="/pricing"
            data-testid="my-free-lessons-trial-cta"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            Start 7-day trial
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : null}

      {/* Cap-reached nudge */}
      {!isPaid && quota && !quota.unlimited && quota.remaining === 0 ? (
        <div
          data-testid="my-free-lessons-cap-nudge"
          className="mt-8 rounded-xl border border-[#e6a63a]/40 bg-[#fdf7e6] p-5 sm:p-6"
        >
          <p className="text-sm font-extrabold text-[#7a5a12] leading-snug">
            You&apos;ve used your 5 free lessons this month.
          </p>
          <p className="mt-1 text-xs text-[#7a5a12]/85 font-medium leading-relaxed">
            Your free allowance resets on the 1st. Start a 7-day free trial to keep generating
            today — full engine, slide decks, exports, planner, and every subject unlocked.
          </p>
          <Link
            to="/pricing"
            data-testid="my-free-lessons-cap-trial-cta"
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.14em] text-white hover:bg-[#132030]"
          >
            Start 7-day trial
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : null}
    </div>
  );
};

export default MyFreeLessons;
